import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { expressMutation, useExpressQuery } from '../../../../repository/react-query/useExpress';
import { useContext } from 'react';
import { NotificationContext } from '../../../../NotificationContext';

/**
 * Custom hook to fetch pail weights data with optional filtering
 *
 * @param {Object} options - Query options
 * @param {string} [options.date] - Optional date filter in YYYY-MM format (e.g., "2025-09")
 *                                  Returns records with dates less than the provided value
 * @param {string} [options.producer_id] - Optional producer ID to filter results
 * @param {string} [options.enabled] - Optional parameter to disable the query
 *
 * @example
 * // Fetch all pail weights for a specific producer
 * const { data, isLoading } = useRecentPailWeights({ producer_id: "prod123" });
 *
 * @example
 * // Fetch pail weights before September 2025 for a specific producer
 * const { data, isLoading } = useRecentPailWeights({
 *   date: "2025-09",
 *   producer_id: "prod123"
 * });
 */
export const useGetRecentPailWeights = ({ date, producer_id, enabled }) => {
    return useQuery({
        queryKey: ['pail-weights', date, producer_id],
        queryFn: useExpressQuery({ endpoint: '/pail-weights/recent', options: { params: { date, producer_id } } }),
        enabled: enabled ?? true,
    });
};

/**
 * Custom hook for updating pail weight records.
 *
 * @example
 * const { mutate, isLoading } = useMutatePailWeight();
 *
 * // Create a pail weight
 * mutate({
 *   weight: 25.5,
 *   producer_id: "producer123",
 *   date: "2025-04"
 * });
 *
 * // Update a pail weight
 * mutate({
 *   _id: "12345",
 *   weight: 45,
 *   producer_id: "producer123",
 *   date: "2025-04"
 * });
 */
export const useMutatePailWeight = () => {
    const queryClient = useQueryClient();
    const { showSnackbar } = useContext(NotificationContext);
    return useMutation({
        mutationFn: async ({ _id, date, producer_id, weight }) =>
            expressMutation({
                method: 'put',
                endpoint: '/pail-weights',
                body: {
                    _id,
                    date,
                    producer_id,
                    weight,
                },
            }),
        onMutate: async (newItem) => {
            // Cancel any ongoing refetches to avoid overwiriting optimistic update
            await queryClient.cancelQueries({ queryKey: ['pail-weights'] });
            const queries = queryClient.getQueryCache().findAll({ queryKey: ['pail-weights'] });

            queries.forEach((query) => {
                const previousData = query.state.data;

                if (Array.isArray(previousData)) {
                    queryClient.setQueryData(
                        query.queryKey,
                        previousData.map((item) => (item._id === newItem._id ? newItem : item))
                    );
                } else if (previousData && typeof previousData === 'object') {
                    queryClient.setQueryData(query.queryKey, newItem);
                }
            });

            return { queries };
        },
        onError: (err, _, ctx) => {
            // Rollback to previous data
            if (ctx?.queries) {
                ctx.queries.forEach((query) => {
                    queryClient.setQueryData(query.queryKey, query.state.data);
                });
            }

            // Show error
            const res = err?.response?.data;
            if (res?.name === 'ValidationError' && res?.details?.body?.[0].context.key === 'weight') {
                showSnackbar({ severity: 'error', message: 'Invalid weight. Please try again.' });
            } else {
                showSnackbar({ severity: 'error', message: 'An error occurred while updating the pail weight. Please try again or contact support if the problem persists.' });
            }
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: ['pail-weights'] });
        },
    });
};
